import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ChevronLeft, MapPin, Users, CheckCircle2, TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";

const PATH = "/blog/case-study-1800-pax-dubai-corporate-conference";
const TITLE = "How We Managed a 1,800-Pax Corporate Conference in Dubai";
const DESCRIPTION =
  "Behind the scenes of a 1,800-pax 5-day global sales conference operated by Trawelldmc across 11 Dubai hotels — the brief, the logistics challenge, the dynamic solutions, the ROI.";
const PUBLISHED_ISO = "2026-02-16";
const PUBLISHED_LABEL = "Feb 16, 2026";
const AUTHOR = "Trawelldmc Team";
const IMAGE =
  "https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=webp&w=1600&q=80";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog" },
  { name: "Case Study: 1,800-Pax Dubai Conference", path: PATH },
];

const ARTICLE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: TITLE,
  description: DESCRIPTION,
  image: IMAGE,
  datePublished: PUBLISHED_ISO,
  dateModified: PUBLISHED_ISO,
  author: { "@type": "Organization", name: AUTHOR, url: "https://www.trawelldmc.com" },
  publisher: {
    "@type": "Organization",
    name: "Trawelldmc",
    logo: { "@type": "ImageObject", url: "https://www.trawelldmc.com/trawelldmc-logo-hd.png" },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.trawelldmc.com${PATH}` },
};

const KPIS = [
  { label: "Delegates", value: "1,800" },
  { label: "Duration", value: "5 days" },
  { label: "Hotels", value: "11" },
  { label: "Countries of origin", value: "47" },
];

export default function CaseStudy1800Dubai() {
  return (
    <div data-testid="case-study-1800-dubai">
      <Seo
        path={PATH}
        title={`${TITLE} | Trawelldmc Case Study`}
        description={DESCRIPTION}
        type="article"
        image={IMAGE}
        breadcrumb={BREADCRUMB}
        extraJsonLd={ARTICLE_JSONLD}
      />

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8">
            <ChevronLeft className="h-4 w-4" /> Back to Blog
          </Link>
          <div className="overline !text-[#FF5238] mb-5">Case Study · Dubai DMC</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08]">
            {TITLE}
          </h1>
          <div className="mt-8 flex items-center gap-5 text-sm text-white/60 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {PUBLISHED_LABEL}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> 8 min read</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Dubai, UAE</span>
            <span>By {AUTHOR}</span>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 -mt-10 mb-12">
          <img
            src={IMAGE}
            alt="Trawelldmc 1,800-pax corporate conference Dubai — MICE case study"
            width="1600"
            height="900"
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover rounded-md shadow-xl"
          />
        </div>
      </section>

      {/* KPI STRIP */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 mb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {KPIS.map((k) => (
            <div key={k.label} className="card-flat p-6 text-center">
              <div className="font-heading text-3xl font-light text-[#FF5238]">{k.value}</div>
              <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{k.label}</div>
            </div>
          ))}
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="text-xl text-slate-700 leading-relaxed font-light">
          When the regional VP of a Fortune 500 enterprise SaaS company briefed us on their global
          sales kick-off, the parameters were simple to state and brutally complex to deliver:
          1,800 delegates from 47 countries, 5 days, one production-grade gala dinner, four
          breakout tracks, and zero room for a single missed transfer. Here&apos;s how Trawelldmc&apos;s
          Dubai DMC team turned the brief into a delivered programme — and what corporate planners
          can learn from it.
        </p>

        {/* THE BRIEF */}
        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">01.</span> The Brief
        </h2>
        <p className="text-slate-700 leading-relaxed">
          The client needed a Dubai-hosted global sales conference in late Q1 — peak Dubai season,
          when major hotel inventory routinely sells out 9+ months ahead. The non-negotiables:
        </p>
        <ul className="text-slate-700 leading-relaxed mt-4 space-y-2 list-disc pl-6">
          <li><strong>1,800 delegates</strong> arriving on 47 inbound flights across 72 hours</li>
          <li><strong>Single plenary venue</strong> capable of 1,800 theatre-style + tiered seating</li>
          <li><strong>Multi-property hotel block</strong> with 5-star quality and walking-distance proximity</li>
          <li><strong>Themed gala dinner</strong> at an iconic Dubai venue — &ldquo;story your delegates will tell&rdquo;</li>
          <li><strong>Visa support</strong> for delegates from 47 different passport jurisdictions</li>
          <li><strong>Full AV, content production, translation booths</strong> for 6 languages</li>
        </ul>
        <p className="text-slate-700 leading-relaxed mt-4">
          Budget: USD 4,850 per pax inclusive. Lead time: 7 months from brief to event day.
        </p>

        {/* THE CHALLENGE */}
        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">02.</span> Logistics Challenge
        </h2>
        <p className="text-slate-700 leading-relaxed">
          Three logistics challenges defined this programme. First, the inventory squeeze — at
          1,800 rooms, no single Dubai property was large enough, so we had to architect an
          11-hotel cluster within a 12-minute coach radius of the plenary. Second, the arrival
          spike — 1,800 pax across 47 flights compressed into 72 hours means our airport teams
          were running a near-continuous meet-and-greet operation for three days straight. Third,
          the gala venue — booking an iconic Dubai venue capable of seating 1,800 for plated F&B,
          with custom branded production, at 7 months&apos; notice required relationships, not just
          contracts.
        </p>

        {/* THE SOLUTION */}
        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">03.</span> Dynamic Solutions Implemented
        </h2>
        <p className="text-slate-700 leading-relaxed">
          We solved each challenge with the kind of local relationships only a Dubai-headquartered
          DMC with 20+ years of operating depth can leverage.
        </p>
        <ul className="text-slate-700 leading-relaxed mt-4 space-y-3 list-none pl-0">
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Plenary at Dubai World Trade Centre Sheikh Saeed Halls</strong> — 1,800-pax theatre with adjacent breakout halls. Booked 7 months out via direct senior-team relationship.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>11-hotel cluster</strong> — Mövenpick Grand Al Bustan, Crowne Plaza Festival City, Pullman Deira City Centre, Marriott Marquis, and seven more — all within 12-minute coach distance with synchronised check-in protocols.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Airport ops team of 32</strong> running rolling meet-and-greet across DXB Terminals 1 & 3 for 72 hours, with 28 coaches in rotation.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Gala at Atlantis The Palm — Asateer Tent</strong> — themed Arabian Nights production with branded LED, live entertainment, and 8-course Middle Eastern tasting menu.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Visa coordination</strong> for 47 jurisdictions handled in-house — 6-week processing window with weekly status reports to the client&apos;s HR team.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>AV & content production</strong> — 22-meter LED main stage, simultaneous interpretation in 6 languages, full multi-camera capture for post-event highlight reel.</span></li>
        </ul>

        {/* THE RESULTS */}
        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">04.</span> Final ROI / Results
        </h2>
        <div className="bg-[#FDFCF8] border-l-4 border-[#FF5238] p-7 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <TrendingUp className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">96/100 NPS</div>
              <div className="text-sm text-slate-600 mt-1">Post-event delegate satisfaction score</div>
            </div>
            <div>
              <Users className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">0 missed transfers</div>
              <div className="text-sm text-slate-600 mt-1">Across 1,800 pax / 72-hour arrival window</div>
            </div>
            <div>
              <CheckCircle2 className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">Under budget</div>
              <div className="text-sm text-slate-600 mt-1">Delivered at USD 4,710 per pax vs 4,850 brief</div>
            </div>
            <div>
              <Calendar className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">Client rebooked</div>
              <div className="text-sm text-slate-600 mt-1">Awarded a follow-on 2,200-pax 2027 conference</div>
            </div>
          </div>
        </div>
        <p className="text-slate-700 leading-relaxed mt-8">
          The client&apos;s post-event survey returned a 96/100 NPS — the highest the company had ever
          recorded for a global sales kick-off. The CFO&apos;s office confirmed final settlement at
          USD 4,710 per pax against the briefed budget of 4,850. And in the most meaningful
          metric of all, the client awarded us their follow-on 2,200-pax 2027 conference 11 days
          after the event closed.
        </p>

        {/* CTA */}
        <div className="mt-12 p-8 bg-[#0B192C] text-white rounded-md">
          <div className="overline !text-[#FF5238] mb-3">Brief us</div>
          <h3 className="font-heading text-2xl font-medium tracking-tight">
            Have a similar Asia DMC brief?
          </h3>
          <p className="text-white/80 mt-3 leading-relaxed">
            Talk to a senior B2B account manager. We respond within 48 hours with a costed,
            themed proposal — Dubai, Singapore, Thailand, or any of our seven Asia DMC markets.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/inquiry?type=MICE" className="btn-primary inline-flex">
              Submit an RFP <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/dubai-dmc" className="btn-secondary !bg-white/10 !border-white/20 !text-white hover:!bg-white/20">
              Read our Dubai DMC services
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
